/**
 * 要素のクリックイベントを管理するクラス（ブレイクポイント対応版）
 * @example
 * const clickManager = new OnClickManager();
 * clickManager.init();
 * clickManager.initModalBackground();
 * 
 * // 範囲指定の例
 * clickManager.init('.onClick', '#specific-container');
 * clickManager.initModalBackground('.modalbg', '.modal-wrapper');
 */
export class OnClickManager {
    /**
     * キーボードハンドラーを作成する（Enter、Spaceキー対応）
     * @param {Element} element - キーボードイベントを設定する要素
     * @param {Function} clickHandler - 対応するクリックハンドラー
     * @returns {Function} キーボードハンドラー関数
     * @private
     */
    createKeydownHandler(element, clickHandler) {
        return (e) => {
            // EnterキーまたはSpaceキーが押された場合
            if (e.key === 'Enter' || e.key === ' ') {
                // デフォルトの動作を防ぐ（Spaceキーのスクロールなど）
                e.preventDefault();
                // クリックハンドラーを実行
                clickHandler(e);
            }
        };
    }

    /**
     * OnClickManagerのコンストラクタ
     * @param {boolean} debugMode - デバッグモードを有効にするか（デフォルト: false）
     */
    constructor(debugMode = false) {
        // デバッグモード設定
        this.debugMode = debugMode;
        // ブレイクポイントの定義
        this.breakPoints = {
            pc: 1025,  // 1025px以上
            sp: 1024   // 1024px以下
        };

        // アクション定義を格納するオブジェクト
        this.actions = {
            "modal-opt-video": (isOn, element) => {
                const video = element.querySelector('video');
                if (video) {
                    if (isOn) {
                        // モーダルを開く時：最初に戻してから再生
                        video.currentTime = 0;
                        video.play();
                    } else {
                        // モーダルを閉じる時：一時停止して最初に戻す
                        video.pause();
                        video.currentTime = 0;
                    }
                }
            },
            "modal-opt-youtube": (isOn, element) => {
                const iframe = element.querySelector(`iframe`);
                if (iframe) {
                    if (isOn) {
                        // モーダルを開く時：最初に戻してから再生
                        iframe.contentWindow.postMessage(
                            `{"event":"command","func":"seekTo","args":[0, true]}`,
                            '*'
                        );
                        // 少し遅延させてから再生（seekToの完了を待つ）
                        setTimeout(() => {
                            iframe.contentWindow.postMessage(
                                `{"event":"command","func":"playVideo","args":""}`,
                                '*'
                            );
                        }, 100);
                    } else {
                        // モーダルを閉じる時：一時停止して最初に戻す
                        iframe.contentWindow.postMessage(
                            `{"event":"command","func":"pauseVideo","args":""}`,
                            '*'
                        );
                        iframe.contentWindow.postMessage(
                            `{"event":"command","func":"seekTo","args":[0, true]}`,
                            '*'
                        );
                    }
                }
            },
            // スクロールロックのアクションを追加
            "modal-scroll-lock": (isOn, element) => {
                const body = document.body;
                if (isOn) {
                    body.style.overflow = "clip";
                    body.classList.add("modal-is-shown")
                } else {
                    body.style.overflow = "";
                    body.classList.remove("modal-is-shown")
                }
            },
            // フォーカス管理のアクション（モーダル開閉時にフォーカスを移動）
            "modal-opt-remember-focus": (isOn, element) => {
                if (isOn) {
                    // 現在のフォーカス要素を記憶
                    element._previouslyFocusedElement = document.activeElement;
                    // モーダルにフォーカスを移す（少し遅らせて確実に移す）
                    setTimeout(() => {
                        element.querySelector(`*`).focus();
                    }, 10);
                } else {
                    // 記憶していた要素にフォーカスを戻す
                    if (element._previouslyFocusedElement &&
                        typeof element._previouslyFocusedElement.focus === 'function') {
                        element._previouslyFocusedElement.focus();
                        element._previouslyFocusedElement = null;
                    }
                }
            },
            "modal-opt-inert": (isOn, element, e) => {
                if (isOn) {
                    element.removeAttribute("inert");
                } else {
                    element.setAttribute("inert", "");
                }
            },
            "modal-add-class-to-btn": (isOn, element, e) => {
                const btns = document.querySelectorAll(`.onClick[data-elm="${element.id}"]`)
                btns.forEach((btn) => {
                    if (isOn) {
                        btn.classList.add(`onclick-is-on`)
                    } else {
                        btn.classList.remove(`onclick-is-on`)
                    }
                })
            },
            "modal-never-shown": (isOn, element) => {
                window.localStorage.setItem(`onclick-never-shown-${element.id}`, true);
            },
            "onclick-toggle-motion": (isOn, element, e) => {
                try {
                    if (isOn) {
                        document.documentElement.setAttribute('data-pause-motion', '1');
                    } else {
                        document.documentElement.removeAttribute('data-pause-motion');
                    }

                    document.body.dispatchEvent(new CustomEvent("on-change-data-pause-motion", {
                        detail: {
                            paused: isOn,
                        }
                    }));
                } catch (error) {
                    console.warn(error)
                }
            },
        };

        // クリーンアップ用アクション定義
        this.cleanupActions = {
            "modal-scroll-lock": (element) => {
                document.body.style.overflow = "";
            },
            "modal-opt-remember-focus": (element) => {
                // 記憶していたフォーカス要素があれば戻す
                if (element._previouslyFocusedElement &&
                    typeof element._previouslyFocusedElement.focus === 'function') {
                    element._previouslyFocusedElement.focus();
                    element._previouslyFocusedElement = null;
                }
            },
            "modal-opt-inert": (element) => {
                element.removeAttribute("inert");
            },
            "modal-add-class-to-btn": (element) => {
                // この場合はボタンのクラスなので特別な処理は不要
            },
            "modal-opt-video": (element) => {
                const video = element.querySelector('video');
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
            },
            "modal-opt-youtube": (element) => {
                const iframe = element.querySelector(`iframe`);
                if (iframe) {
                    // 一時停止して最初に戻す
                    iframe.contentWindow.postMessage(
                        `{"event":"command","func":"pauseVideo","args":""}`,
                        '*'
                    );
                    iframe.contentWindow.postMessage(
                        `{"event":"command","func":"seekTo","args":[0, true]}`,
                        '*'
                    );
                }
            },
        };

        // 初期化時のアクション定義
        this.initActions = {
            "modal-never-shown": (element) => {
                if (window.localStorage.getItem(`onclick-never-shown-${element.id}`)) {
                    element.classList.add("is-on");
                }
            },
            // 初期化時にinert状態を確認する（is-onがない場合はinertを付ける）
            "modal-opt-inert": (element) => {
                // is-onクラスがない場合はinertを付ける（デフォルトで非表示状態）
                if (!element.classList.contains("is-on")) {
                    element.setAttribute("inert", "");
                }
            },
        };

        // イベントリスナーを保持するための Map
        this.eventListeners = new Map();

        // 初期化済み要素を記録するSet（initActionsを実行済みの要素）
        this.initializedElements = new Set();

        // initActions実行済みを記録するSet（新規追加）
        this.initActionsExecuted = new Set();

        // ブレイクポイント管理用
        this.disabledElements = new Set(); // 現在無効化されている要素
        this.resizeHandler = null;

        // 初期化
        this.setupResizeListener();
    }

    /**
     * リサイズリスナーを設定
     * @private
     */
    setupResizeListener() {
        this.resizeHandler = () => {
            this.checkBreakPoints();
        };
        window.addEventListener('resize', this.resizeHandler);
        // 初回チェック
        this.checkBreakPoints();
    }

    /**
     * デバッグログを出力する
     * @param {...any} args - ログに出力する内容
     * @private
     */
    debugLog(...args) {
        if (this.debugMode) {
            console.log('[OnClickManager]', ...args);
        }
    }

    /**
     * 現在の画面幅をチェックして要素の有効/無効を切り替え
     * @private
     */
    checkBreakPoints() {
        const currentWidth = window.innerWidth;
        this.debugLog('checkBreakPoints called, currentWidth:', currentWidth);

        // data-onclick-enable-break-point属性を持つ要素をチェック
        document.querySelectorAll('[data-onclick-enable-break-point]').forEach(element => {
            const breakPoint = element.dataset.onclickEnableBreakPoint;
            const shouldBeEnabled = this.shouldElementBeEnabled(breakPoint, currentWidth);
            const isCurrentlyDisabled = this.disabledElements.has(element);

            this.debugLog('Element:', element.id || element.className,
                'breakPoint:', breakPoint,
                'shouldBeEnabled:', shouldBeEnabled,
                'isCurrentlyDisabled:', isCurrentlyDisabled);

            if (shouldBeEnabled && isCurrentlyDisabled) {
                // 有効化
                this.debugLog('Enabling element:', element.id || element.className);
                this.enableElement(element);
            } else if (!shouldBeEnabled && !isCurrentlyDisabled) {
                // 無効化
                this.debugLog('Disabling element:', element.id || element.className);
                this.disableElement(element);
            }
        });
    }

    /**
     * ブレイクポイントに基づいて要素が有効であるべきかを判定
     * @param {string} breakPoint - 'pc' または 'sp'
     * @param {number} currentWidth - 現在の画面幅
     * @returns {boolean} 有効であるべきかどうか
     * @private
     */
    shouldElementBeEnabled(breakPoint, currentWidth) {
        switch (breakPoint) {
            case 'pc':
                return currentWidth >= this.breakPoints.pc;
            case 'sp':
                return currentWidth <= this.breakPoints.sp;
            default:
                return true; // 不明な値の場合は有効
        }
    }

    /**
     * 要素を無効化
     * @param {Element} element - 無効化する要素
     * @private
     */
    disableElement(element) {
        this.debugLog('disableElement called for:', element.id || element.className, 'has is-on:', element.classList.contains('is-on'));

        // is-onクラスがある場合はクリーンアップアクションを実行
        if (element.classList.contains('is-on')) {
            this.debugLog('Executing cleanup actions for:', element.id || element.className);
            this.executeCleanupActions(element);
            element.classList.remove('is-on');
        }

        // is-onクラスがなくても、アクティブな状態のクリーンアップが必要な場合がある
        // （例：スクロールロックが有効になっているが、まだis-onがない場合など）
        const hasActiveActions = element.className
            .split(" ")
            .some(className => this.cleanupActions.hasOwnProperty(className));

        if (hasActiveActions) {
            this.debugLog('Executing cleanup actions (no is-on) for:', element.id || element.className);
            this.executeCleanupActions(element);
        }

        this.disabledElements.add(element);
    }

    /**
     * 要素を有効化
     * @param {Element} element - 有効化する要素
     * @private
     */
    enableElement(element) {
        this.debugLog('enableElement called for:', element.id || element.className);

        this.disabledElements.delete(element);

        // initActionsをまだ実行していない場合は実行する
        const elementId = element.id;
        if (elementId && !this.initActionsExecuted.has(elementId)) {
            this.debugLog('Executing init actions for enabled element:', elementId);
            this.executeInitActions(element);
            this.initActionsExecuted.add(elementId);
        }
    }

    /**
     * initアクションを実行
     * @param {Element} element - 初期化対象の要素
     * @private
     */
    executeInitActions(element) {
        const actionClasses = element.className
            .split(" ")
            .filter((className) =>
                this.initActions.hasOwnProperty(className)
            );

        this.debugLog('executeInitActions for:', element.id || element.className, 'actionClasses:', actionClasses);

        actionClasses.forEach((actionClass) => {
            this.debugLog('Executing init action:', actionClass);
            try {
                this.initActions[actionClass](element);
            } catch (error) {
                console.error('[OnClickManager] Error in init action:', actionClass, error);
            }
        });
    }

    /**
     * クリーンアップアクションを実行
     * @param {Element} element - クリーンアップ対象の要素
     * @private
     */
    executeCleanupActions(element) {
        const actionClasses = element.className
            .split(" ")
            .filter((className) =>
                this.cleanupActions.hasOwnProperty(className)
            );

        this.debugLog('executeCleanupActions for:', element.id || element.className, 'actionClasses:', actionClasses);

        actionClasses.forEach((actionClass) => {
            this.debugLog('Executing cleanup action:', actionClass);
            try {
                this.cleanupActions[actionClass](element);
            } catch (error) {
                console.error('[OnClickManager] Error in cleanup action:', actionClass, error);
            }
        });
    }

    /**
 * セレクターと範囲から要素を取得する
 * @param {string} selector - 検索するセレクター
 * @param {string|Element|NodeList|HTMLCollection} scope - 検索範囲（セレクター文字列、Element、またはNodeList）
 * @returns {NodeList|Element[]} 見つかった要素のリスト
 * @private
 */
    getElementsInScope(selector, scope) {
        if (!scope) {
            // 範囲指定なしの場合はdocument全体から検索
            return document.querySelectorAll(selector);
        }

        // scopeがNodeListまたはHTMLCollectionの場合
        if (scope instanceof NodeList || scope instanceof HTMLCollection) {
            const results = [];
            scope.forEach((element) => {
                const found = element.querySelectorAll(selector);
                results.push(...found);
            });
            return results;
        }

        // scopeが文字列の場合は要素を取得
        const scopeElement = typeof scope === 'string'
            ? document.querySelector(scope)
            : scope;

        if (!scopeElement) {
            console.warn('[OnClickManager] Scope element not found:', scope);
            return document.querySelectorAll(selector);
        }

        // 範囲内から要素を検索
        return scopeElement.querySelectorAll(selector);
    }


    /**
     * アクションを追加する
     * @param {string} name - アクション名
     * @param {Function} handler - アクションハンドラー
     * @param {Function} cleanupHandler - クリーンアップハンドラー（省略可）
     * @example
     * clickManager.addAction('customAction', 
     *   (isOn, element) => { console.log('Action', isOn); },
     *   (element) => { console.log('Cleanup'); }
     * );
     */
    addAction(name, handler, cleanupHandler = null) {
        this.actions[name] = handler;
        if (cleanupHandler) {
            this.cleanupActions[name] = cleanupHandler;
        }
    }

    /**
     * 初期化アクションを追加する
     * @param {string} name - アクション名
     * @param {Function} handler - アクションハンドラー
     * @example
     * clickManager.addInitAction('customInit', (element) => {
     *   console.log('Custom init action', element);
     * });
     */
    addInitAction(name, handler) {
        this.initActions[name] = handler;
    }

    /**
     * 指定された要素にクリックイベントとキーボードイベントを設定する
     * @param {string} selector - クリックイベントを設定する要素のセレクター
     * @param {string|Element} scope - 検索範囲（セレクター文字列またはElement、省略時はdocument全体）
     * @example
     * // document全体から.onClickを検索
     * clickManager.init('.onClick');
     * 
     * // 特定のコンテナ内のみから検索
     * clickManager.init('.onClick', '#main-container');
     * clickManager.init('.onClick', document.querySelector('.specific-area'));
     */
    init(selector = ".onClick", scope = null) {
        const elements = this.getElementsInScope(selector, scope);

        this.debugLog('init called with selector:', selector, 'scope:', scope, 'found elements:', elements.length);

        elements.forEach((element) => {
            // クリックイベントの設定
            const clickHandler = this.createClickHandler(element);
            element.addEventListener("click", clickHandler);
            this.eventListeners.set(element, {
                type: "click",
                handler: clickHandler
            });

            // キーボードイベントの設定（Enter、Space）
            const keydownHandler = this.createKeydownHandler(element, clickHandler);
            element.addEventListener("keydown", keydownHandler);
            // 既存のイベントリスナーに追加で保存
            const existingListener = this.eventListeners.get(element);
            this.eventListeners.set(element, {
                ...existingListener,
                keydown: keydownHandler
            });

            // tabindex属性がない場合は追加（キーボードフォーカス可能にする）
            if (!element.hasAttribute('tabindex')) {
                element.setAttribute('tabindex', '0');
            }

            // data-elmで指定された要素を取得
            const targetId = element.dataset.elm;
            if (targetId && targetId !== "myself") {
                const targetElement = document.querySelector(`#${targetId}`);
                if (!targetElement) return;

                // まだ初期化されていない要素の場合のみ処理を実行
                if (!this.initializedElements.has(targetId)) {
                    // ブレイクポイントをチェック
                    const breakPoint = targetElement.dataset.onclickEnableBreakPoint;
                    const currentWidth = window.innerWidth;
                    const shouldBeEnabled = breakPoint ? this.shouldElementBeEnabled(breakPoint, currentWidth) : true;

                    if (shouldBeEnabled) {
                        // 有効な場合はinitActionsを実行
                        if (!this.initActionsExecuted.has(targetId)) {
                            this.debugLog('Executing init actions on init for:', targetId);
                            this.executeInitActions(targetElement);
                            this.initActionsExecuted.add(targetId);
                        }
                    } else {
                        // ブレイクポイントで無効な場合は無効化リストに追加
                        this.disabledElements.add(targetElement);
                        this.debugLog('Element disabled on init due to breakpoint:', targetId);
                    }

                    // 初期化済みとしてマーク
                    this.initializedElements.add(targetId);
                }
            }
        });

        // 初期化後にブレイクポイントチェック
        this.checkBreakPoints();
    }

    /**
     * すべてのイベントリスナーを削除する
     * @example
     * clickManager.unmount();
     */
    unmount() {
        // thisが存在しない場合は何もしない
        if (!this) return;

        // リサイズイベントリスナーを削除
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        // is-onクラスを削除
        this.removeIsOnClasses();

        // 通常のクリックイベントとキーボードイベントの削除
        if (this.eventListeners) {
            this.eventListeners.forEach((listener, element) => {
                if (element && element.removeEventListener) {
                    // クリックイベントの削除
                    if (listener.handler) {
                        element.removeEventListener("click", listener.handler);
                    }
                    // キーボードイベントの削除
                    if (listener.keydown) {
                        element.removeEventListener("keydown", listener.keydown);
                    }
                }
            });
            this.eventListeners.clear();
        }

        // 初期化済み要素のSetをクリア
        if (this.initializedElements) {
            this.initializedElements.clear();
        }

        // initActions実行済みのSetをクリア
        if (this.initActionsExecuted) {
            this.initActionsExecuted.clear();
        }

        // 無効化された要素のSetをクリア
        if (this.disabledElements) {
            this.disabledElements.clear();
        }
    }

    /**
     * 全てのis-onクラスを削除する
     * @private
     */
    removeIsOnClasses() {
        // .onClickが設定された要素のis-onクラスを削除
        const clickElements = document.querySelectorAll('.onClick.is-on');
        clickElements.forEach(element => {
            // クリーンアップアクションを実行してからクラス削除
            this.executeCleanupActions(element);
            element.classList.remove('is-on');
        });

        // .modalbgが設定された要素のis-onクラスを削除
        const modalBgElements = document.querySelectorAll('.modalbg.is-on');
        modalBgElements.forEach(element => {
            this.executeCleanupActions(element);
            element.classList.remove('is-on');
        });

        // data-elmで指定された要素のis-onクラスを削除
        const elmElements = document.querySelectorAll('[data-elm]');
        elmElements.forEach(element => {
            const targetId = element.dataset.elm;
            if (targetId && targetId !== 'myself') {
                const targetElement = document.querySelector(`#${targetId}`);
                if (targetElement && targetElement.classList.contains('is-on')) {
                    this.executeCleanupActions(targetElement);
                    targetElement.classList.remove('is-on');
                }
            }
        });

        // 一般的なis-onクラスも削除（念のため）
        const allIsOnElements = document.querySelectorAll('.is-on');
        allIsOnElements.forEach(element => {
            this.executeCleanupActions(element);
            element.classList.remove('is-on');
        });

        // inert属性を削除
        const inertElements = document.querySelectorAll('[inert]');
        inertElements.forEach(element => {
            element.removeAttribute('inert');
        });
    }

    /**
 * 初期化時に登録されたすべての開いている要素を閉じる
 * initで範囲指定された要素のみが対象となる
 * @example
 * // initで登録されたモーダルやトグル要素のみを閉じる
 * clickManager.closeAll();
 */
    closeAll() {
        this.debugLog('closeAll called');

        // 処理済みの要素を記録するSet（重複処理を防ぐ）
        const processedElements = new Set();

        // eventListenersに登録されている要素を処理
        this.eventListeners.forEach((listener, element) => {
            // ブレイクポイントで無効化されている要素はスキップ
            if (this.disabledElements.has(element)) {
                this.debugLog('Skipping disabled element:', element.id || element.className);
                return;
            }

            // data-elmで指定されたターゲット要素を取得
            const targetId = element.dataset.elm;

            if (targetId === "myself") {
                // 自分自身がターゲットの場合
                if (element.classList.contains('is-on') && !processedElements.has(element)) {
                    this.debugLog('Closing element (myself):', element.id || element.className);
                    this.executeCleanupActions(element);
                    element.classList.remove('is-on');
                    element.classList.remove('onclick-is-on');
                    processedElements.add(element);
                }
            } else if (targetId) {
                // 別の要素がターゲットの場合
                const targetElement = document.querySelector(`#${targetId}`);
                if (targetElement && !processedElements.has(targetElement)) {
                    // ブレイクポイントで無効化されている要素はスキップ
                    if (this.disabledElements.has(targetElement)) {
                        this.debugLog('Skipping disabled target element:', targetElement.id || targetElement.className);
                        return;
                    }

                    if (targetElement.classList.contains('is-on')) {
                        this.debugLog('Closing target element:', targetElement.id || targetElement.className);
                        this.executeCleanupActions(targetElement);
                        targetElement.classList.remove('is-on');
                        processedElements.add(targetElement);
                    }

                    // 対応するボタンのクラスも削除
                    element.classList.remove('onclick-is-on');
                }
            }

            // modalbgの場合の処理
            if (element.classList.contains('modalbg') && element.classList.contains('is-on')) {
                if (!processedElements.has(element)) {
                    this.debugLog('Closing modal background:', element.id || element.className);
                    this.executeCleanupActions(element);
                    element.classList.remove('is-on');
                    processedElements.add(element);
                }
            }
        });

        this.debugLog('closeAll completed, processed elements:', processedElements.size);
    }

    /**
     * クリックハンドラーを作成する
     * @param {Element} element - クリックイベントを設定する要素
     * @returns {Function} クリックハンドラー関数
     * @private
     */
    createClickHandler(element) {
        return (e) => {
            // data-only-me属性のチェックを追加
            const isOnlyMe = element.hasAttribute("data-only-me");

            // only-meが設定されていて、クリックされた要素が直接の対象でない場合は処理をスキップ
            if (isOnlyMe && e.target !== element) return;

            const isMyselfTarget = element.dataset.elm === "myself";

            if (isMyselfTarget) {
                if (e.target !== e.currentTarget) return;

                // ブレイクポイントチェック
                if (this.disabledElements.has(element)) return;

                const isOn = !element.classList.contains("is-on");
                element.classList.toggle("is-on");

                // アクションクラスの処理
                const actionClasses = element.className
                    .split(" ")
                    .filter((className) =>
                        this.actions.hasOwnProperty(className)
                    );

                actionClasses.forEach((actionClass) => {
                    this.actions[actionClass](isOn, element, e);
                });
            } else {
                const targetElement = document.querySelector(
                    `#${element.dataset.elm}`
                );
                if (!targetElement) return;

                // ブレイクポイントチェック（ターゲット要素に対して）
                if (this.disabledElements.has(targetElement)) return;

                const isOn = !targetElement.classList.contains("is-on");
                targetElement.classList.toggle("is-on");

                // アクションクラスの処理
                const actionClasses = targetElement.className
                    .split(" ")
                    .filter((className) =>
                        this.actions.hasOwnProperty(className)
                    );

                actionClasses.forEach((actionClass) => {
                    this.actions[actionClass](isOn, targetElement, e);
                });
            }
        };
    }

    /**
     * モーダル背景のクリックイベントを設定する
     * @param {string} selector - モーダル背景要素のセレクター
     * @param {string|Element} scope - 検索範囲（セレクター文字列またはElement、省略時はdocument全体）
     * @example
     * // document全体から.modalbgを検索
     * clickManager.initModalBackground('.modalbg');
     * 
     * // 特定のコンテナ内のみから検索
     * clickManager.initModalBackground('.modalbg', '#modal-container');
     * clickManager.initModalBackground('.modalbg', document.querySelector('.modal-wrapper'));
     */
    initModalBackground(selector = ".modalbg", scope = null) {
        const modalBackgrounds = this.getElementsInScope(selector, scope);

        this.debugLog('initModalBackground called with selector:', selector, 'scope:', scope, 'found elements:', modalBackgrounds.length);

        modalBackgrounds.forEach((bg) => {
            const handler = (e) => {
                if (e.target !== e.currentTarget) return;

                // ブレイクポイントチェック
                if (this.disabledElements.has(bg)) return;

                const isOn = !bg.classList.contains("is-on");
                bg.classList.toggle("is-on");

                const actionClasses = bg.className
                    .split(" ")
                    .filter((className) =>
                        this.actions.hasOwnProperty(className)
                    );

                actionClasses.forEach((actionClass) => {
                    this.actions[actionClass](isOn, bg, e);
                });
            };

            bg.addEventListener("click", handler);
            // イベントリスナーを保存
            this.eventListeners.set(bg, { type: "click", handler });
        });

        // 初期化後にブレイクポイントチェック
        this.checkBreakPoints();
    }
}

// 通常モード（本番用）
const clickManager = new OnClickManager();
clickManager.init(undefined, document.documentElement);
clickManager.initModalBackground();

// console.log(clickManager);

// デバッグモード（開発用）
// const clickManager = new OnClickManager(true);
// clickManager.init();
// clickManager.initModalBackground();

// 範囲指定の使用例
// clickManager.init('.onClick', '#specific-container'); // 特定のコンテナ内のみ
// clickManager.init('.onClick', document.querySelector('.dynamic-content')); // Element直接指定
// clickManager.initModalBackground('.modalbg', '.modal-area'); // モーダル背景も範囲指定

// コンポーネントのアンマウント時に呼び出す
// clickManager.unmount();